import { unsafeCSS, html } from "lit";
import { state, query } from "lit/decorators.js";
import { e } from "../../scoped-registry-mixin-2fqf-E5n.js";
import { LeafElement } from "../../components/leaf-element.js";
import { LeafBackdrop } from "../../components/backdrop/backdrop.component.js";
import { LeafIcon } from "../../components/icon/icon.component.js";
import { I18nController } from "../../utilities/i18n.js";
import { debounce } from "../../utilities/debounce.js";
const styles = '@charset "UTF-8";\n/* ------------------------------------*\\\n    #TYPOGRAPHY PLACEHOLDERS\n\\*------------------------------------ */\n/*\n  Hiding class, making content visible only to screen readers but not visually ("sr" meaning "screen-reader")\n  Is the hidden content read by a screen reader?	- Yes\n  Will the hidden element affect the document layout? - No\n  Will the hidden element’s box model be rendered? - No\n  Does the element detect clicks or focus? - Yes\n  https://css-tricks.com/comparing-various-ways-to-hide-things-in-css/#method-5-the-visually-hidden-class\n*/\n:host {\n  --button-width-height: calc(var(--leaf-spacing-48) + var(--leaf-spacing-8));\n  --container-width-height: calc(var(--leaf-spacing-120) + var(--leaf-spacing-4));\n  --container-bottom: var(--leaf-spacing-32);\n  --container-right: var(--leaf-spacing-16);\n  --container-transition: 0.4s;\n  display: block;\n  height: 100%;\n  width: 100%;\n}\n:host .chc-c-back-to-top {\n  bottom: var(--container-bottom);\n  cursor: pointer;\n  display: block;\n  height: var(--container-width-height);\n  opacity: 0;\n  pointer-events: none;\n  position: fixed;\n  right: var(--container-right);\n  transition: var(--container-transition);\n  width: var(--container-width-height);\n  z-index: var(--leafdcws-z-index-50);\n}\n:host .chc-c-back-to-top.show {\n  opacity: 1 !important;\n  pointer-events: auto;\n}\n@media (max-width: 59.999rem) {\n  :host .chc-c-back-to-top:active button {\n    transform: rotate(0deg);\n  }\n}\n@media (min-width: 60rem) {\n  :host .chc-c-back-to-top:hover button {\n    transform: rotate(0deg);\n  }\n}\n:host .chc-c-back-to-top__button {\n  font-family: var(--leaf-typography-meta-small-font-family);\n  font-size: var(--leaf-typography-meta-small-font-size);\n  font-weight: var(--leaf-typography-meta-small-font-weight);\n  line-height: var(--leaf-typography-meta-small-line-height);\n  -webkit-text-decoration: var(--leaf-typography-meta-small-text-decoration);\n          text-decoration: var(--leaf-typography-meta-small-text-decoration);\n  text-transform: var(--leaf-typography-meta-small-text-transform);\n  font-weight: var(--leaf-typography-label-small-font-weight);\n  align-items: center;\n  background-color: var(--leaf-color-button-primary-bg);\n  border: var(--leaf-border-width-focus) solid var(--leaf-color-border-knockout);\n  border-radius: var(--leaf-border-radius-round);\n  color: var(--leaf-color-button-primary-content);\n  cursor: pointer;\n  flex-direction: column;\n  left: 2.25rem;\n  width: var(--button-width-height);\n  height: var(--button-width-height);\n  position: relative;\n  text-decoration: none;\n  text-transform: uppercase;\n  top: 1.75rem;\n  transform: rotate(-30deg);\n  transform-origin: bottom left;\n  transition: transform 0.2s ease-out;\n  -webkit-user-select: none;\n     -moz-user-select: none;\n          user-select: none;\n}\n:host .chc-c-back-to-top__button:hover {\n  background-color: var(--leaf-color-button-primary-bg-hover);\n  color: var(--leaf-color-button-primary-content-hover);\n}\n:host .chc-c-back-to-top__button:focus {\n  background-color: var(--leaf-color-button-primary-bg-hover);\n  color: var(--leaf-color-button-primary-content-hover);\n  outline: var(--leaf-border-width-focus) solid var(--leaf-color-border-knockout);\n  outline-offset: var(--leaf-border-width-focus);\n  box-shadow: 0 0 0 var(--leaf-border-width-focus) var(--leaf-color-button-primary-outline);\n}\n:host .chc-c-back-to-top__button:active {\n  background-color: var(--leaf-color-button-primary-bg-active);\n  color: var(--leaf-color-button-primary-content-active);\n  outline: none;\n}\n:host .chc-c-back-to-top__button:disabled {\n  background-color: var(--button-bg-disabled);\n  color: var(--button-color-disabled);\n}\n:host .chc-c-back-to-top__icon {\n  --icon-height: var(--leaf-spacing-24);\n  --icon-width: var(--leaf-spacing-24);\n}';
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
const _CHCBackToTop = class _CHCBackToTop extends e(LeafElement) {
  constructor() {
    super(...arguments);
    this.i18nController = new I18nController(this);
    this.active = false;
    this.interval = 0;
    this.checkScrollLocation = () => {
      const ARIA_HIDDEN = "aria-hidden";
      if (window.scrollY === 0) {
        this.active = false;
        clearInterval(this.interval);
        this._container.classList.remove("show");
        this._container.setAttribute(ARIA_HIDDEN, "true");
        this._button.setAttribute("tabindex", "-1");
        if (!window.location.hash) {
          window.location.href = "#";
        }
      }
    };
  }
  connectedCallback() {
    super.connectedCallback();
    this.checkScrollLocation = this.checkScrollLocation.bind(this);
    this.handleScroll = debounce(this.handleScroll.bind(this), 200);
    window.addEventListener("scroll", this.handleScroll);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("scroll", this.handleScroll);
  }
  handleScroll() {
    const ARIA_HIDDEN = "aria-hidden";
    if (window.scrollY !== 0) {
      this._container.classList.add("show");
      this._container.setAttribute(ARIA_HIDDEN, "false");
      this._button.removeAttribute("tabindex");
    } else {
      this._container.classList.remove("show");
      this._container.setAttribute(ARIA_HIDDEN, "true");
      this._button.setAttribute("tabindex", "-1");
    }
  }
  handleClick() {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth"
    });
    this.active = true;
    document.body.style.overflowY = "scroll";
    this._button.blur();
    this.interval = window.setInterval(this.checkScrollLocation, 75);
  }
  render() {
    return html`
      <div class="chc-c-back-to-top">
        <button class="chc-c-back-to-top__button" @click="${this.handleClick}">
          <leaf-icon
            class="chc-c-back-to-top__icon"
            set="system"
            name="arrow-upward"
            label="${this.i18nController.term("back_to_top")}"
          ></leaf-icon>
          ${this.i18nController.term("back_to_top")}
        </button>
      </div>
      <leaf-backdrop ?active="${this.active}"></leaf-backdrop>
    `;
  }
};
_CHCBackToTop.styles = unsafeCSS(styles);
_CHCBackToTop.elementDefinitions = {
  "leaf-icon": LeafIcon,
  "leaf-backdrop": LeafBackdrop
};
let CHCBackToTop = _CHCBackToTop;
__decorateClass([
  state()
], CHCBackToTop.prototype, "active", 2);
__decorateClass([
  state()
], CHCBackToTop.prototype, "interval", 2);
__decorateClass([
  query(".chc-c-back-to-top")
], CHCBackToTop.prototype, "_container", 2);
__decorateClass([
  query(".chc-c-back-to-top__button")
], CHCBackToTop.prototype, "_button", 2);
export {
  CHCBackToTop
};
//# sourceMappingURL=back-to-top.component.js.map
